import { Button } from "@/components/ui/button"
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useNavigate } from "react-router-dom"
import { LoginUserSchema } from "@/utils/schemas/auth"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { useMutation } from "@tanstack/react-query"
import { useAppDispatch } from "@/app/hooks"
import { axiosClient } from "@/api/axiosClient"
import { setAccessToken } from "@/app/features/admin/authSlice"
import { setAdmin } from "@/app/features/admin/adminSlice"
import { Eye, EyeOff } from "lucide-react"
import { useState } from "react"
import { toast } from "react-toastify"
import logo from '../assets/logo-freemochat.png'
import logoDark from '../assets/logo-freemochat-dark.png'


export function AdminLogin() {
    const { register, handleSubmit, formState: { errors } } = useForm({ resolver: zodResolver(LoginUserSchema) })
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const [showPassword, setShowPassword] = useState(false)
    const [isLoading, setIsLoading] = useState(false)

    const loginAdmin = async (data: any) => {
        const response = await axiosClient.post("/admin/login", data, { withCredentials: true })
        return response.data
    }

    const mutation = useMutation({
        mutationFn: async (data): Promise<any> => {
            return await loginAdmin(data)
        },
        onSuccess: (data) => {
            dispatch(setAccessToken(data.access_token))
            dispatch(setAdmin(data.user))
            toast.success("Login successful")
            navigate("/")
        },
        onError: (error: any) => {
            setIsLoading(false)
            const errorMessage = error?.response?.data?.message || "Invalid username or password"
            toast.error(errorMessage)
        }
    })

    const onSubmit = async (data: any) => {
        setIsLoading(true)
        mutation.mutate(data)
    }

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword)
    }


    return (
        <div className="flex flex-col gap-10 items-center justify-center w-screen h-screen">
            <h1 className="text-2xl font-bold">
                <img className="h-16 sm:h-20 w-auto dark:hidden" src={logo} alt="Freemochat" />
                <img className="h-16 sm:h-20 w-auto hidden dark:block" src={logoDark} alt="Freemochat" />
            </h1>
            <Card className="mx-auto max-w-md w-full">
                <CardHeader>
                    <CardTitle className="text-2xl">Admin Login</CardTitle>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="grid gap-4">
                            <div className="grid gap-2">
                                <Label htmlFor="username">Username</Label>
                                <Input
                                    id="username"
                                    type="text"
                                    placeholder="Enter your username"
                                    disabled={isLoading}
                                    {...register("username")}
                                    required
                                />
                                {errors.username && (
                                    <p className="text-sm text-red-500">{errors.username.message as string}</p>
                                )}
                            </div>
                            <div className="grid gap-2">
                                <Label htmlFor="password">Password</Label>
                                <div className="relative">
                                    <Input
                                        id="password"
                                        type={showPassword ? "text" : "password"}
                                        placeholder="Enter your password"
                                        disabled={isLoading}
                                        {...register("password")}
                                        required
                                    />
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center hover:bg-transparent"
                                        onClick={togglePasswordVisibility}
                                        disabled={isLoading}
                                    >
                                        {showPassword ? (
                                            <EyeOff className="h-4 w-4 text-gray-500" />
                                        ) : (
                                            <Eye className="h-4 w-4 text-gray-500" />
                                        )}
                                    </Button>
                                </div>
                                {errors.password && (
                                    <p className="text-sm text-red-500">{errors.password.message as string}</p>
                                )}
                            </div>
                            <Button type="submit" className="w-full" disabled={isLoading}>
                                {isLoading ? (
                                    <svg
                                        className="animate-spin"
                                        viewBox="0 0 64 64"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                    >
                                        <path
                                            d="M32 3C35.8083 3 39.5794 3.75011 43.0978 5.20749C46.6163 6.66488 49.8132 8.80101 52.5061 11.4939C55.199 14.1868 57.3351 17.3837 58.7925 20.9022C60.2499 24.4206 61 28.1917 61 32C61 35.8083 60.2499 39.5794 58.7925 43.0978C57.3351 46.6163 55.199 49.8132 52.5061 52.5061C49.8132 55.199 46.6163 57.3351 43.0978 58.7925C39.5794 60.2499 35.8083 61 32 61C28.1917 61 24.4206 60.2499 20.9022 58.7925C17.3837 57.3351 14.1868 55.199 11.4939 52.5061C8.801 49.8132 6.66487 46.6163 5.20749 43.0978C3.7501 39.5794 3 35.8083 3 32C3 28.1917 3.75011 24.4206 5.2075 20.9022C6.66489 17.3837 8.80101 14.1868 11.4939 11.4939C14.1868 8.80099 17.3838 6.66487 20.9022 5.20749C24.4206 3.7501 28.1917 3 32 3L32 3Z"
                                            stroke="currentColor"
                                            strokeWidth="8"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="text-gray-400"
                                        />
                                        <path
                                            d="M32 3C36.5778 3 41.0906 4.08374 45.1692 6.16256C49.2477 8.24138 52.7762 11.2562 55.466 14.9605C58.1558 18.6647 59.9304 22.9531 60.6448 27.4748C61.3591 31.9965 60.9928 36.6232 59.5759 40.9762"
                                            stroke="currentColor"
                                            strokeWidth="8"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="text-white"
                                        />
                                    </svg>
                                ) : (
                                    "Login"
                                )}
                            </Button>
                        </div>
                    </form>
                </CardContent>
            </Card>
        </div>
    )
}
